import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useApi } from '../../hooks/useApi'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import { Shield, ShieldAlert, User, ChevronDown, Clock, CheckCircle, XCircle } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserPermission {
  module: string
  enabled: boolean
}

interface UserAiStats {
  totalTokensIn: number
  totalTokensOut: number
  totalCost: number
  conversationCount: number
}

interface AdminUser {
  id: number
  email: string
  name: string
  picture: string | null
  role: string
  status: string
  approvedAt: string | null
  createdAt: string
  permissions: UserPermission[]
  aiStats: UserAiStats
}

const STATUS_STYLES: Record<string, string> = {
  approved: 'bg-green-50 text-green-700 border-green-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  blocked: 'bg-red-50 text-red-600 border-red-200',
}

const STATUS_ICONS: Record<string, typeof CheckCircle> = {
  approved: CheckCircle,
  pending: Clock,
  blocked: XCircle,
}

const STATUS_LABELS: Record<string, string> = {
  approved: 'Approuvé',
  pending: 'En attente',
  blocked: 'Bloqué',
}

const MODULES = [
  { key: 'ai_analyst', label: 'AI Analyst' },
  { key: 'llm_visibility', label: 'Visibilit\u00e9 LLM' },
  { key: 'rankings', label: 'Rankings' },
  { key: 'audits', label: 'Audits' },
  { key: 'local', label: 'Local' },
  { key: 'reports', label: 'Rapports' },
]

const ROLES = ['user', 'admin', 'blocked'] as const

const ROLE_STYLES: Record<string, string> = {
  admin: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  user: 'bg-gray-50 text-gray-600 border-gray-200',
  blocked: 'bg-red-50 text-red-600 border-red-200',
}

const ROLE_ICONS: Record<string, typeof Shield> = {
  admin: Shield,
  user: User,
  blocked: ShieldAlert,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function apiPatch<T>(url: string, body: unknown): Promise<{ data?: T; error?: string }> {
  const res = await fetch(url, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = await res.json()
  if (!res.ok || json.success === false) return { error: json.error ?? 'Erreur' }
  return { data: json.data ?? json }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatCost(cost: number): string {
  return cost.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })
}

// ---------------------------------------------------------------------------
// Access Status Badge + Approve button
// ---------------------------------------------------------------------------

function AccessBadge({
  userId,
  status,
  onChanged,
}: {
  userId: number
  status: string
  onChanged: () => void
}) {
  const [loading, setLoading] = useState(false)
  const Icon = STATUS_ICONS[status] ?? Clock

  async function handleApprove() {
    if (!window.confirm('Approuver cet utilisateur ?')) return
    setLoading(true)
    await apiPatch(`/api/admin/access/users/${userId}/status`, { status: 'approved' })
    setLoading(false)
    onChanged()
  }

  async function handleBlock() {
    if (!window.confirm('Bloquer l\'accès de cet utilisateur ?')) return
    setLoading(true)
    await apiPatch(`/api/admin/access/users/${userId}/status`, { status: 'blocked' })
    setLoading(false)
    onChanged()
  }

  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] ?? STATUS_STYLES.pending}`}>
        <Icon size={11} strokeWidth={1.5} />
        {STATUS_LABELS[status] ?? status}
      </span>
      {status === 'pending' && (
        <button
          type="button"
          onClick={handleApprove}
          disabled={loading}
          className="text-xs text-green-700 font-medium hover:underline disabled:opacity-50"
        >
          Approuver
        </button>
      )}
      {status === 'approved' && (
        <button
          type="button"
          onClick={handleBlock}
          disabled={loading}
          className="text-xs text-red-500 hover:underline disabled:opacity-50"
        >
          Bloquer
        </button>
      )}
      {status === 'blocked' && (
        <button
          type="button"
          onClick={handleApprove}
          disabled={loading}
          className="text-xs text-green-700 font-medium hover:underline disabled:opacity-50"
        >
          Réactiver
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Toggle Switch
// ---------------------------------------------------------------------------

function Toggle({
  enabled,
  loading,
  onChange,
}: {
  enabled: boolean
  loading: boolean
  onChange: () => void
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={loading}
      className={`
        relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1
        ${enabled ? 'bg-indigo-500' : 'bg-gray-300'}
        ${loading ? 'opacity-50 cursor-wait' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform
          transition-transform duration-200 ease-in-out
          ${enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}
        `}
      />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Role Dropdown
// ---------------------------------------------------------------------------

function RoleDropdown({
  userId,
  currentRole,
  onChanged,
}: {
  userId: number
  currentRole: string
  onChanged: () => void
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleChange(role: string) {
    if (role === currentRole) {
      setOpen(false)
      return
    }
    if (role === 'blocked' && !window.confirm('Bloquer cet utilisateur ?')) {
      setOpen(false)
      return
    }
    setLoading(true)
    await apiPatch(`/api/admin/users/${userId}/status`, { role })
    setLoading(false)
    setOpen(false)
    onChanged()
  }

  const Icon = ROLE_ICONS[currentRole] ?? User

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={loading}
        className={`
          inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium
          transition-colors hover:bg-opacity-80
          ${ROLE_STYLES[currentRole] ?? ROLE_STYLES.user}
          ${loading ? 'opacity-50 cursor-wait' : ''}
        `}
      >
        <Icon size={13} strokeWidth={1.25} />
        {currentRole}
        <ChevronDown size={12} strokeWidth={1.25} />
      </button>

      {open && (
        <>
          {/* backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-32 rounded-lg border border-[#E2E4ED] bg-white py-1 shadow-lg">
            {ROLES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => handleChange(r)}
                className={`
                  block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50
                  ${r === currentRole ? 'font-semibold text-indigo-600' : 'text-[#1B2559]'}
                `}
              >
                {r}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function Users() {
  const { isAdmin, loading: userLoading } = useCurrentUser()
  const { data: users, loading, error, refetch } = useApi<AdminUser[]>('/api/admin/users')
  const [pendingToggles, setPendingToggles] = useState<Set<string>>(new Set())

  // Admin guard
  if (!userLoading && !isAdmin) {
    return <Navigate to="/projects" replace />
  }

  async function handleToggle(userId: number, module: string, currentEnabled: boolean) {
    const key = `${userId}-${module}`
    setPendingToggles((prev) => new Set(prev).add(key))
    await apiPatch(`/api/admin/users/${userId}/permissions`, { module, enabled: !currentEnabled })
    setPendingToggles((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    refetch()
  }

  if (loading || userLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-center text-red-600">
        Erreur lors du chargement des utilisateurs : {error}
      </div>
    )
  }

  const list = users ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2559]">Gestion des utilisateurs</h1>
          <p className="mt-1 text-sm text-[#8B8FA7]">{list.length} utilisateur{list.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-[#E2E4ED] bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[#E2E4ED] bg-[#F8F9FC]">
              <th className="px-5 py-3 font-medium text-[#8B8FA7]">Utilisateur</th>
              <th className="px-5 py-3 font-medium text-[#8B8FA7]">Accès</th>
              <th className="px-5 py-3 font-medium text-[#8B8FA7]">Role</th>
              <th className="px-5 py-3 font-medium text-[#8B8FA7]">Inscription</th>
              {MODULES.map((m) => (
                <th key={m.key} className="px-3 py-3 text-center font-medium text-[#8B8FA7]">{m.label}</th>
              ))}
              <th className="px-5 py-3 font-medium text-[#8B8FA7]">Conso IA</th>
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-b border-[#E2E4ED] last:border-b-0 hover:bg-[#F8F9FC]/50">
                {/* Avatar + Name + Email */}
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img
                        src={u.picture}
                        alt={u.name}
                        className="h-8 w-8 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-600">
                        {(u.name || u.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-medium text-[#1B2559]">{u.name || '-'}</p>
                      <p className="truncate text-xs text-[#8B8FA7]">{u.email}</p>
                    </div>
                  </div>
                </td>

                {/* Access status */}
                <td className="px-5 py-3">
                  <AccessBadge userId={u.id} status={u.status ?? 'pending'} onChanged={refetch} />
                </td>

                {/* Role */}
                <td className="px-5 py-3">
                  <RoleDropdown userId={u.id} currentRole={u.role} onChanged={refetch} />
                </td>

                {/* Date */}
                <td className="px-5 py-3 text-[#8B8FA7]">{formatDate(u.createdAt)}</td>

                {/* Module toggles */}
                {MODULES.map((m) => {
                  const perm = u.permissions.find((p) => p.module === m.key)
                  const enabled = perm?.enabled ?? false
                  const toggleKey = `${u.id}-${m.key}`
                  return (
                    <td key={m.key} className="px-3 py-3 text-center">
                      <Toggle
                        enabled={enabled}
                        loading={pendingToggles.has(toggleKey)}
                        onChange={() => handleToggle(u.id, m.key, enabled)}
                      />
                    </td>
                  )
                })}

                {/* AI Stats */}
                <td className="px-5 py-3">
                  <p className="font-medium text-[#1B2559]">{formatCost(u.aiStats.totalCost)}</p>
                  <p className="text-xs text-[#8B8FA7]">{u.aiStats.conversationCount} conversation{u.aiStats.conversationCount > 1 ? 's' : ''}</p>
                </td>
              </tr>
            ))}

            {list.length === 0 && (
              <tr>
                <td colSpan={4 + MODULES.length + 1} className="px-5 py-10 text-center text-[#8B8FA7]">
                  Aucun utilisateur trouve
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
